/** Daily pickup PIN helpers (UI + soft API verify). */

import { getResolvedTenantId } from '../config/api-config';

export type LocalPickupPinRecord = {
  pin: string;
  studentId: string;
  validDate: string;
  issuedAt: string;
  expiresAt: string;
  usedAt?: string | null;
  qrPayload?: string | null;
};

const STORAGE_KEY = 'skoolfix.pickupPins.v1';

export function normalizePickupPin(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

export function looksLikePickupPin(value: string): boolean {
  const n = normalizePickupPin(value);
  return n.length === 4 || n.length === 6;
}

/** Pull a PIN from a typed value or scanned QR URL/payload. */
export function extractPickupPinFromScan(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const pathMatch = trimmed.match(/\/verify\/pickup\/([^/?#]+)/i);
  if (pathMatch?.[1]) {
    try {
      return normalizePickupPin(decodeURIComponent(pathMatch[1]));
    } catch {
      return normalizePickupPin(pathMatch[1]);
    }
  }
  const queryMatch = trimmed.match(/[?&](?:pin|code)=([^&]+)/i);
  if (queryMatch?.[1]) {
    try {
      return normalizePickupPin(decodeURIComponent(queryMatch[1]));
    } catch {
      return normalizePickupPin(queryMatch[1]);
    }
  }
  return normalizePickupPin(trimmed);
}

/** Deterministic demo PIN from student id + local date (legacy fallback). */
export function deriveDemoPickupPin(studentId: string, dateIso: string): string {
  const seed = `${studentId}:${dateIso}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return String(100000 + (hash % 900000));
}

export function generateRandomPickupPin(): string {
  const n = Math.floor(100000 + Math.random() * 900000);
  return String(n);
}

export function pickupPinVerifyUrl(code: string): string {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return `/verify/pickup/${encodeURIComponent(code)}`;
  }
  return `${window.location.origin}/verify/pickup/${encodeURIComponent(code)}`;
}

/**
 * Normalize API/local qr_payload into an absolute verify URL for QR encoding + WhatsApp.
 * Backend often returns a relative path (`/verify/pickup/367644`) which phones cannot open.
 */
export function resolvePickupQrPayload(
  payload: string | null | undefined,
  pin?: string | null
): string {
  const raw = (payload || '').trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/')) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${raw}`;
    }
    return raw;
  }
  const fromPayload = extractPickupPinFromScan(raw);
  const code = looksLikePickupPin(fromPayload)
    ? fromPayload
    : normalizePickupPin((pin || '').trim());
  if (looksLikePickupPin(code)) return pickupPinVerifyUrl(code);
  return raw;
}

/** End of local calendar day as ISO-like local timestamp for display/compare. */
export function endOfLocalDayIso(dateIso: string): string {
  return `${dateIso}T23:59:59`;
}

export function isPickupPinExpired(expiresAt: string | null | undefined, now = new Date()): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt.includes('T') ? expiresAt : `${expiresAt}T23:59:59`);
  if (Number.isNaN(ms)) return false;
  return now.getTime() > ms;
}

export function formatPickupPinExpiry(expiresAt: string | null | undefined): string {
  if (!expiresAt) return 'today';
  const ms = Date.parse(expiresAt.includes('T') ? expiresAt : `${expiresAt}T23:59:59`);
  if (Number.isNaN(ms)) return expiresAt;
  return new Date(ms).toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    day: 'numeric',
    month: 'short',
  });
}

type StoreShape = Record<string, LocalPickupPinRecord[]>;

function tenantKey(): string {
  try {
    return getResolvedTenantId() || 'default';
  } catch {
    return 'default';
  }
}

function readStore(): StoreShape {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoreShape;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store: StoreShape) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function listForTenant(): LocalPickupPinRecord[] {
  return readStore()[tenantKey()] ?? [];
}

function saveForTenant(rows: LocalPickupPinRecord[]) {
  const store = readStore();
  store[tenantKey()] = rows.slice(0, 200);
  writeStore(store);
}

export function getActiveLocalPickupPin(
  studentId: string,
  validDate: string
): LocalPickupPinRecord | null {
  const rows = listForTenant()
    .filter((r) => r.studentId === studentId && r.validDate === validDate && !r.usedAt)
    .filter((r) => !isPickupPinExpired(r.expiresAt))
    .sort((a, b) => (a.issuedAt < b.issuedAt ? 1 : -1));
  return rows[0] ?? null;
}

export function findLocalPickupPinByCode(
  pin: string,
  validDate: string
): LocalPickupPinRecord | null {
  const code = normalizePickupPin(pin);
  if (!code) return null;
  const rows = listForTenant()
    .filter((r) => r.pin === code && r.validDate === validDate)
    .sort((a, b) => (a.issuedAt < b.issuedAt ? 1 : -1));
  return rows[0] ?? null;
}

export function saveLocalPickupPin(record: LocalPickupPinRecord): LocalPickupPinRecord {
  const rows = listForTenant().filter(
    (r) => !(r.studentId === record.studentId && r.validDate === record.validDate && !r.usedAt)
  );
  rows.unshift(record);
  saveForTenant(rows);
  return record;
}

export function markLocalPickupPinUsed(
  pin: string,
  validDate: string,
  usedAt = new Date().toISOString()
): LocalPickupPinRecord | null {
  const code = normalizePickupPin(pin);
  const rows = listForTenant();
  let updated: LocalPickupPinRecord | null = null;
  const next = rows.map((r) => {
    if (r.pin === code && r.validDate === validDate && !r.usedAt) {
      updated = { ...r, usedAt };
      return updated;
    }
    return r;
  });
  if (updated) saveForTenant(next);
  return updated;
}

export function issueLocalPickupPin(params: {
  studentId: string;
  validDate: string;
  pin?: string;
  expiresAt?: string | null;
  qrPayload?: string | null;
}): LocalPickupPinRecord {
  const pin = params.pin || generateRandomPickupPin();
  const expiresAt = params.expiresAt || endOfLocalDayIso(params.validDate);
  return saveLocalPickupPin({
    pin,
    studentId: params.studentId,
    validDate: params.validDate,
    issuedAt: new Date().toISOString(),
    expiresAt,
    usedAt: null,
    qrPayload: resolvePickupQrPayload(params.qrPayload, pin),
  });
}
